import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
// Keyboard plugin listeners are registered eagerly in initializeCapacitor() below.
// Events are dispatched on window as 'capacitor-keyboard-show'/'capacitor-keyboard-hide'
// and consumed by the useNativeKeyboard() hook in use-keyboard-visible.ts.
// Accessory bar hiding is done via native iOS code in AppDelegate.swift (not the plugin).
import * as Sentry from '@sentry/react';
import { isCapacitor, purchases } from '@/services/capacitor';
import { Capacitor } from '@capacitor/core';
import { setPendingInvite, clearPendingInvite } from '@/lib/invite-storage';
import { getAuth } from 'firebase/auth';
import { apiUrl } from '@/config/api';

// Listen for notification click navigation from service worker (web only).
// When a user taps a push notification while the app is open in a browser tab,
// the service worker posts a NOTIFICATION_CLICK message with the target URL.
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.url) {
      console.log('📱 [SW Message] Notification click → navigating to:', event.data.url);
      window.location.href = event.data.url;
    }
  });
}

// Initialize Capacitor plugins when the app starts
export const initializeCapacitor = async () => {
  // Add capacitor-native class to html element for native-specific styling
  // This enables CSS that prevents iOS rubber-banding and improves native feel
  if (Capacitor.isNativePlatform()) {
    document.documentElement.classList.add('capacitor-native');
    document.documentElement.classList.add(`capacitor-${Capacitor.getPlatform()}`);
    console.log('[Capacitor] Added native CSS classes:', {
      classes: document.documentElement.classList.toString(),
      platform: Capacitor.getPlatform()
    });
  }

  if (!isCapacitor) return;

  try {
    // Configure Sentry for mobile platforms
    // Note: Sentry is already initialized in main.tsx
    // We just add mobile-specific context here
    console.log('[GlitchTip Mobile] Configuring mobile context...', {
      platform: Capacitor.getPlatform(),
      isNative: Capacitor.isNativePlatform(),
      mode: import.meta.env.MODE,
    });

    // Set global tags for mobile
    Sentry.setTags({
      platform: Capacitor.getPlatform(),
      native: Capacitor.isNativePlatform() ? 'true' : 'false',
      device_type: 'mobile',
    });

    // Set mobile context
    Sentry.setContext('device', {
      platform: Capacitor.getPlatform(),
      native: Capacitor.isNativePlatform(),
    });

    console.log('[GlitchTip Mobile] ✅ Mobile context configured');
    // Hide splash screen when app is ready
    await SplashScreen.hide();

    // Initialize Status Bar
    await initializeStatusBar();

    // Initialize Keyboard plugin listeners for keyboard-aware UI.
    // Events are dispatched on window so the useNativeKeyboard() hook can pick them up
    // without needing its own dynamic import of the plugin.
    try {
      const { Keyboard } = await import('@capacitor/keyboard');
      Keyboard.addListener('keyboardWillShow', (info) => {
        window.dispatchEvent(new CustomEvent('capacitor-keyboard-show', { detail: info }));
      });
      Keyboard.addListener('keyboardWillHide', () => {
        window.dispatchEvent(new CustomEvent('capacitor-keyboard-hide'));
      });
    } catch {
      // Keyboard plugin not available — keyboard-aware UI will use visualViewport fallback
    }

    // Initialize RevenueCat for in-app purchases
    const revenueCatKey = import.meta.env.VITE_REVENUECAT_API_KEY;
    if (revenueCatKey) {
      await purchases.initialize(revenueCatKey);
    }

    // Handle app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        console.log('App has become active');
        // Refresh data when app becomes active
        window.dispatchEvent(new Event('app-resume'));
      } else {
        console.log('App has become inactive');
      }
    });

    // Handle back button on Android
    App.addListener('backButton', ({ canGoBack }) => {
      if (!canGoBack) {
        App.exitApp();
      } else {
        window.history.back();
      }
    });

    // Handle deep links
    App.addListener('appUrlOpen', async (data) => {
      console.log('🔗 [Deep Link] App opened with URL:', data.url);

      // Handle safe-mode deep link: yapzap://safe-mode
      // Triggered from Safari when completely locked out — resets to store build
      if (data.url === 'yapzap://safe-mode' || data.url.startsWith('yapzap://safe-mode')) {
        console.log('🔴 [Deep Link] Safe mode requested — resetting to store build');
        localStorage.setItem('yapzap_prefer_native_build', 'true');
        try {
          const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
          await CapacitorUpdater.reset({ toLastSuccessful: false }).catch(() => {});
        } catch (e) {
          console.error('[Deep Link] Safe mode reset error:', e);
        }
        setTimeout(() => window.location.reload(), 300);
        return;
      }

      // Handle YouTube OAuth callback (yapzap://auth/youtube/callback?success=true or ?tokens=...)
      if (data.url.includes('auth/youtube/callback')) {
        console.log('🎥 [Deep Link] YouTube callback detected');
        try {
          const url = new URL(data.url);
          const successParam = url.searchParams.get('success');
          const tokensParam = url.searchParams.get('tokens');
          const error = url.searchParams.get('error');

          if (error) {
            console.error('🎥 [Deep Link] YouTube OAuth error:', error);
            // Dispatch error event for any listening components
            window.dispatchEvent(new CustomEvent('youtube-oauth-error', { detail: { error } }));
            return;
          }

          // New flow: Backend already saved tokens, just got success=true
          if (successParam === 'true') {
            console.log('🎥 [Deep Link] YouTube connection successful (tokens saved by backend)');
            // Dispatch success event for any listening components
            window.dispatchEvent(new CustomEvent('youtube-oauth-success', { detail: { success: true } }));
            // Also dispatch a general auth status change event
            window.dispatchEvent(new Event('music-auth-changed'));
            return;
          }

          // Legacy flow: Tokens passed via deep link, need to save them
          if (tokensParam) {
            console.log('🎥 [Deep Link] Found tokens (legacy flow), decoding...');
            // Decode the base64 tokens
            const tokens = JSON.parse(atob(decodeURIComponent(tokensParam)));
            console.log('🎥 [Deep Link] Tokens decoded:', {
              hasAccessToken: !!tokens.access_token,
              hasRefreshToken: !!tokens.refresh_token
            });

            // Save the tokens to backend
            const { apiRequest } = await import('./queryClient');
            const saveResponse = await apiRequest('/api/music/youtube/save-tokens', 'POST', {
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
              expiryDate: tokens.expires_in
            });
            console.log('🎥 [Deep Link] Tokens saved:', saveResponse);

            // Dispatch success event for any listening components
            window.dispatchEvent(new CustomEvent('youtube-oauth-success', { detail: { tokens } }));

            // Also dispatch a general auth status change event
            window.dispatchEvent(new Event('music-auth-changed'));
          }
        } catch (e) {
          console.error('🎥 [Deep Link] Error handling YouTube callback:', e);
          window.dispatchEvent(new CustomEvent('youtube-oauth-error', { detail: { error: 'Failed to process tokens' } }));
        }
        return;
      }

      // Handle Tidal OAuth callback (yapzap://auth/tidal/callback?tokens=...)
      if (data.url.includes('auth/tidal/callback')) {
        console.log('🎵 [Deep Link] Tidal callback detected');
        try {
          const url = new URL(data.url);
          const tokensParam = url.searchParams.get('tokens');
          const error = url.searchParams.get('error');

          if (error) {
            console.error('🎵 [Deep Link] Tidal OAuth error:', error);
            window.dispatchEvent(new CustomEvent('tidal-oauth-error', { detail: { error } }));
            return;
          }

          if (tokensParam) {
            console.log('🎵 [Deep Link] Found Tidal tokens, decoding...');
            // Decode the base64 tokens
            const tokens = JSON.parse(atob(decodeURIComponent(tokensParam)));
            console.log('🎵 [Deep Link] Tidal tokens decoded:', {
              hasAccessToken: !!tokens.accessToken,
              hasRefreshToken: !!tokens.refreshToken
            });

            // Save the tokens to backend
            const { apiRequest } = await import('./queryClient');
            const saveResponse = await apiRequest('/api/music/tidal/save-tokens', 'POST', {
              accessToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
              expiresIn: tokens.expiresIn
            });
            console.log('🎵 [Deep Link] Tidal tokens saved:', saveResponse);

            // Dispatch success event for any listening components
            window.dispatchEvent(new CustomEvent('tidal-oauth-success', { detail: { tokens } }));

            // Also dispatch a general auth status change event
            window.dispatchEvent(new Event('music-auth-changed'));
          } else {
            console.error('🎵 [Deep Link] No tokens in Tidal callback');
            window.dispatchEvent(new CustomEvent('tidal-oauth-error', { detail: { error: 'No tokens received' } }));
          }
        } catch (e) {
          console.error('🎵 [Deep Link] Error handling Tidal callback:', e);
          window.dispatchEvent(new CustomEvent('tidal-oauth-error', { detail: { error: 'Failed to process tokens' } }));
        }
        return;
      }

      // Handle iMessage extension auth deep link (yapzap://auth?returnTo=...)
      // This just opens the main app so the user can log in; the returnTo param
      // tells us where they came from in the iMessage extension.
      if (data.url.startsWith('yapzap://auth') && !data.url.includes('auth/youtube') && !data.url.includes('auth/tidal')) {
        console.log('🔗 [Deep Link] iMessage auth request detected, navigating to home for login');
        window.location.href = '/';
        return;
      }

      // Parse the URL and navigate accordingly
      const url = new URL(data.url);

      // For custom URL schemes like yapzap://invite/CODE, the host becomes "invite"
      // and pathname becomes "/CODE". We need to handle both formats:
      // - https://yapzap.com/invite/CODE -> pathname = /invite/CODE
      // - yapzap://invite/CODE -> host = invite, pathname = /CODE
      const isCustomScheme = url.protocol === 'yapzap:';
      const effectivePath = isCustomScheme ? `/${url.host}${url.pathname}` : url.pathname;

      console.log('🔗 [Deep Link] URL parsing:', {
        original: data.url,
        protocol: url.protocol,
        host: url.host,
        pathname: url.pathname,
        effectivePath
      });

      // Handle invite deep links with auto-join logic
      if (effectivePath.startsWith('/invite/')) {
        const inviteCode = effectivePath.split('/invite/')[1];
        console.log('🎫 [Deep Link] Invite code detected:', inviteCode);

        if (inviteCode) {
          await handleInviteDeepLink(inviteCode);
        } else {
          window.location.href = '/invite';
        }
      } else if (effectivePath.startsWith('/leagues/')) {
        // Forward query params (e.g., ?tab=leaderboard, ?round=X) from widget deep links
        // Capacitor's local server strips query params on navigation, so persist them via sessionStorage
        const queryString = url.search || '';
        if (queryString) {
          sessionStorage.setItem('yapzap_deeplink_params', queryString);
        }
        window.location.href = effectivePath + queryString;
      } else if (effectivePath === '/join-success') {
        // Allow direct navigation to join success page
        window.location.href = effectivePath + url.search;
      } else if (effectivePath.startsWith('/daily')) {
        // Daily round deep links from notifications/widgets
        window.location.href = effectivePath + (url.search || '');
      } else if (effectivePath === '/home' || effectivePath === '/') {
        window.location.href = '/';
      } else if (effectivePath.length > 1) {
        // Catch-all: navigate to any recognized app path
        console.log('🔗 [Deep Link] Catch-all navigation to:', effectivePath);
        window.location.href = effectivePath + (url.search || '');
      }
    });

    // Handle invite deep link with auto-join
    async function handleInviteDeepLink(inviteCode: string) {
      console.log('🎫 [Deep Link] Processing invite deep link:', inviteCode);

      try {
        // First, check if user is authenticated (use direct import, no dynamic import)
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (currentUser) {
          console.log('🎫 [Deep Link] User is authenticated, attempting auto-join...');

          // Get the auth token
          const token = await currentUser.getIdToken();

          // First, get the league ID from the invite code (use direct import)
          const leagueResponse = await fetch(apiUrl(`/api/leagues/invite/${inviteCode}`), {
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (!leagueResponse.ok) {
            console.error('🎫 [Deep Link] Invalid invite code');
            window.location.href = `/invite/${inviteCode}`;
            return;
          }

          const leagueData = await leagueResponse.json();
          console.log('🎫 [Deep Link] League data:', leagueData);

          // Try to join the league
          const joinResponse = await fetch(apiUrl(`/api/leagues/${leagueData.id}/join`), {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ inviteCode })
          });

          if (joinResponse.ok) {
            console.log('🎫 [Deep Link] Successfully joined league!');
            // Clear any pending invite
            clearPendingInvite();
            // Navigate to success page
            const encodedName = encodeURIComponent(leagueData.name || '');
            window.location.href = `/join-success?league=${leagueData.id}&name=${encodedName}`;
          } else {
            const errorData = await joinResponse.json().catch(() => ({}));
            console.log('🎫 [Deep Link] Join response:', errorData);

            // If already a member, go directly to the league
            if (errorData.message?.includes('already a member') || joinResponse.status === 409) {
              console.log('🎫 [Deep Link] User is already a member, navigating to league');
              window.location.href = `/leagues/${leagueData.id}`;
            } else {
              // Some other error, show the invite page
              console.log('🎫 [Deep Link] Join failed, showing invite page');
              window.location.href = `/invite/${inviteCode}`;
            }
          }
        } else {
          console.log('🎫 [Deep Link] User not authenticated, storing invite and showing login');
          // Store the invite code for after auth
          setPendingInvite(inviteCode);
          // Navigate to invite page which will show login prompt
          window.location.href = `/invite/${inviteCode}`;
        }
      } catch (error) {
        console.error('🎫 [Deep Link] Error handling invite deep link:', error);
        // Fall back to showing the invite page
        window.location.href = `/invite/${inviteCode}`;
      }
    }

  } catch (error) {
    console.error('Error initializing Capacitor:', error);
  }
};

// Initialize and configure status bar
const initializeStatusBar = async () => {
  try {
    // Set initial status bar style based on theme
    const isDark = document.documentElement.classList.contains('dark');
    await setStatusBarForTheme(isDark);

    // Listen for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isDark = document.documentElement.classList.contains('dark');
          setStatusBarForTheme(isDark);
        }
      });
    });

    // Only observe if document.documentElement is a valid Node
    if (document.documentElement && document.documentElement.nodeType === Node.ELEMENT_NODE) {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    } else {
      console.warn('StatusBar: document.documentElement is not available for MutationObserver');
    }
  } catch (error) {
    console.error('Error initializing Status Bar:', error);
  }
};

// Set status bar style based on theme
const setStatusBarForTheme = async (isDark: boolean) => {
  try {
    if (isDark) {
      // Dark mode: light text on dark background
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#1a1625' }); // Dark purple from theme
    } else {
      // Light mode: dark text on light background
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#f2f2f7' }); // Light background
    }
  } catch (error) {
    console.error('Error setting Status Bar style:', error);
  }
};

// Export function to update status bar when theme changes
export const updateStatusBarForTheme = (isDark: boolean) => {
  if (isCapacitor) {
    setStatusBarForTheme(isDark);
  }
};